import { Center, HStack, Text } from '@chakra-ui/react'
import type { FC, PropsWithChildren } from 'react'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import LazyImage from '@/components/lazy-image/LazyImage'
import { useAppDispatch, useAppSelector } from '@/modules/app/store'
import {
  selectAllGMPGames,
  selectAllSelfGames,
  switchShowTips,
} from '@/modules/game/gameSlice'
import useShushu from '@/utils/hooks/useShushu'
import fallbackIcon from '@/websites/current/public/icon.png'

export const GameIconText: FC<
  PropsWithChildren & {
    game: string
    gameId?: string | undefined | null
    asLink?: boolean
    center?: boolean
  }
> = ({ game, gameId, asLink, center, children }) => {
  const gameType = !!gameId && gameId !== game ? 'gmp' : 'self'
  const selfGameList = useAppSelector(selectAllSelfGames)
  const gmpGameList = useAppSelector(selectAllGMPGames)
  const { setShushuGameReportData } = useShushu()

  const currentGame = useMemo(() => {
    if (gameType === 'gmp') {
      // gmp游戏
      return gmpGameList.find(item => item?.expand?.game_uuid === gameId)
    } else {
      // 自营游戏
      return selfGameList.find(
        item => item.game_name?.toUpperCase() === game?.toUpperCase(),
      )
    }
  }, [gameType, gmpGameList, selfGameList])

  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const onClickGame = useCallback(() => {
    if (
      currentGame?.is_under_maint === 1 ||
      currentGame?.game_status !== 1 ||
      !currentGame?.game_id
    ) {
      dispatch(switchShowTips(currentGame?.maintenance_text || ''))
    } else {
      if (gameType === 'gmp') {
        const gameId = currentGame?.game_id || ''
        setShushuGameReportData({
          from: 'home-rank',
          game_id: currentGame?.game_id || '',
          game_name: currentGame?.game_name || '',
          manufacturer: 'gmp',
        })

        // GMP游戏
        navigate(`/game/self/${gameId}`)
      } else {
        const gameId = game?.toLocaleLowerCase() || ''
        setShushuGameReportData({
          from: 'home-rank',
          game_id: currentGame?.game_id || '',
          game_name: currentGame?.game_name || '',
          manufacturer: 'inhouse',
        })

        navigate(`/game/inhouse/${gameId}`)
      }
    }
  }, [currentGame, dispatch, game, navigate, gameType])

  if (center) {
    return (
      <Center
        position='sticky'
        left='0'
        as={asLink ? 'button' : undefined}
        onClick={onClickGame}
        w='40'
        mx='auto'
      >
        <LazyImage
          borderRadius='sm'
          src={currentGame?.record_icon || fallbackIcon}
          objectFit='cover'
          objectPosition='bottom'
          boxSize='8'
        />
        <Text marginLeft='5px'>{children}</Text>
      </Center>
    )
  } else {
    return (
      <HStack
        position='sticky'
        left='0'
        as={asLink ? 'button' : undefined}
        spacing='3'
        onClick={onClickGame}
        w='40'
        mx='auto'
      >
        <LazyImage
          borderRadius='sm'
          src={currentGame?.record_icon || fallbackIcon}
          objectFit='cover'
          objectPosition='bottom'
          boxSize='8'
        />
        <Text textTransform='capitalize'>{children}</Text>
      </HStack>
    )
  }
}
